import LocalStorageModel from "../prototypes/LocalStorageModel";
import _ from "underscore";
import { app } from "./AppModel";
import EdenredDataModel from "./EdenredDataModel";
import UrlFactory from "../prototypes/UrlFactory";
import formatPrice from "../prototypes/formatPrice";
import PaymentBrandModel from "./PaymentBrandModel";
import type { PaymentModeSelectDatas } from "@src/components/page-content/Checkout/useCheckoutLogic";
import type { ICardOptions } from "@src/components/page-content/Checkout/PaymentMode/OgoneDirectProvider/OgoneDirectAddCardForm";
import type { GooglePayProps } from "@src/components/page-content/Checkout/Payment.utils";
import { EAlertBlockVariant } from "design-system/src/Core/AlertBlock/AlertBlock";
import type { CustomerCard } from "@src/interface/Customer";
import { isCardExpired } from "@src/backbone/hooks/useFormatCardDetails";
//
// Model PaymentGroup
//

export enum PROVIDER_REQUIRE {
  NOTHING = 0,
  PAYMENT_MODE = 1,
  RETURN_MONEY = 10,
  OGONE_ADD_CARD = 20,
  OGONE_CVC = 21,
  OGONE_VALID_BRAND = 22, // la brand est invalide ou manquante
  OGONE_CARD_EXPIRED = 23,
  OGONE_DIRECT_TOKENIZATION_FORM_VALIDATION = 60,
  OGONE_DIRECT_TOKENIZATION_SAVING_CARD = 61,
  REQUIRE_ALT_PM = 70,
  APPLE_PAY_AWAITING_START = 80,
  GOOGLE_PAY_AWAITING_START = 90,
  PRIMARY_ADDRESS = 30,
  SIMPLE_PAYMENT = 40,
  RECAPTCHA_TOKEN = 50,
  MODE_VAD_UNAVAILABLE = 101,
  MODE_VAE_UNAVAILABLE = 102,
}

export enum EPaymentModeDisableDetailsType {
  INVALID_PROVIDER = 1,
  MULTI_PAYMENT_INVALID = 2,
  PRODUCT_RESTRICTION = 3,
  UNAVAILABLE_CARD = 4,
  UNAVAILABLE_STORE = 5,
  CARD_EXPIRED = 6,
  REQUIRE_ALT_PM = 9,
  EDENRED_SELF_SUFFISANT = 10,
  UNAVAILABLE_VAD = 11,
  UNAVAILABLE_VAE = 12,
}

export const GroupedPaymentModeWarnings = [
  EPaymentModeDisableDetailsType.EDENRED_SELF_SUFFISANT,
];

export const ExpiredCardWarning = [EPaymentModeDisableDetailsType.CARD_EXPIRED];

export type PMDisabledDetails = {
  message: string;
  code: EPaymentModeDisableDetailsType;
  variant?: EAlertBlockVariant;
};

const selectOptionTemplate = _.template(
  '<option value="<%= value %>" <%= disabled?\'disabled\':\'\'%> data-id="<%= id %>" data-content="<%= content %>" <%= attr %>><%= name %></option>'
);

enum EPaymentNative {
  APPLE_PAY = "apple_pay",
  GOOGLE_PAY = "google_pay",
}

export enum EPaymentProviders {
  CASH_ON_DELIVERY_PROVIDER = "CashOnDeliveryProvider",
  CB_ON_DELIVERY_PROVIDER = "CBOnDeliveryProvider",
  OGONE_PROVIDER = "OgoneProvider",
  OGONE_DIRECT_PROVIDER = "OgoneDirectProvider",
  PAYTABS_PROVIDER = "PaytabsProvider",
  ZERO_PROVIDER = "ZeroProvider",
  EDENRED_PROVIDER = "EdenredProvider",
}

function formatCardName(rawCardName: any, shortX: boolean = false) {
  return (rawCardName || "")
    .replace(shortX ? /X+/g : /X{3,4}/g, "•••• ")
    .replace(/([\d]{4})/g, "$1 ")
    .replace(/X/gi, "•")
    .trim();
}
function formatExpire(card: any) {
  let expire = card.expiryDate || "";
  if (expire)
    expire = app.t(
      "expire en %s",
      "Checkout",
      expire.replace(/^(\d\d)(\d\d)$/, "$1/$2")
    );
  return expire;
}

export interface PaymentModePicture {
  id_object_picture: string;
  legend: string;
  types: string[];
}

class PaymentModeModel extends LocalStorageModel<{
  id_payment_mode: string | number;
  name: string;
  short_name: string;
  provider: EPaymentProviders;
  id_store: string | undefined;
  vae: boolean;
  vad: boolean;
  params: Record<string, any>;
  pictures: PaymentModePicture[];
  options?: Record<string, any>;
  restrictions?: Record<string, any>;
  priority?: number;
}> {
  name = "PaymentModeModel";
  route = "apiPaymentModeModel";
  idAttribute = "id_payment_mode";

  defaults() {
    return {
      vae: true,
      vad: true,
      pictures: [],
      params: {},
    };
  }

  initialize(attributes?: any, options?: any): void {
    const model = this;
    // ObjectModel constructor heritage
    super.initialize.apply(model, [attributes, options]);
    //
    model.resetOptions();
  }

  getCards(excludeTemporary?: boolean) {
    const model = this,
      id_payment_mode = model.id + "";
    const cards = app.getCustomer().getCards();
    const brands = app.getPaymentBrands();
    const availableBrands = model.getParam("brand");
    const cardsMap: Array<PaymentModeSelectDatas> = [];
    const priority = model.getTokenizedCardPriority();
    cards.each((card) => {
      const expire = formatExpire(card);
      const isExpired = isCardExpired(card);
      const id_card = card.id_card,
        value = id_payment_mode + ":" + id_card,
        cardNum = formatCardName(card.cardNum, true),
        cbBrand = card.brand,
        warnings: PMDisabledDetails[] = [],
        brand = brands.find((b) => {
          // retro-compatibilité projet ORM
          const brand = b.getBrand();
          return (
            brand === cbBrand &&
            +(b.get("id_payment_mode") || 0) === +id_payment_mode
          );
        });
      let disabled = true,
        id_picture = "",
        id_brand = "";
      let cardLabel = "data-cs-mask " + cardNum;
      const cardParams = {
        directlink: 1 as 0 | 1,
        id_card,
        cbBrand,
        cvc: "",
      };
      if (!!expire) {
        cardLabel += " data-cs-mask " + expire;
      }

      if (brand) {
        id_picture = brand.getIdPicture();
        id_brand = brand.id + "";
        disabled = !availableBrands.includes(brand.id);
        // on ignore les cartes si il est soudainement devenu non-directlink...
        if (!brand.get("directlink")) {
          return;
        }
      }
      if (isExpired) {
        disabled = true;
        warnings.push({
          message: model.getParam("unavailable_card"),
          code: EPaymentModeDisableDetailsType.CARD_EXPIRED,
        });
      } else if (disabled) {
        warnings.push({
          message: model.getParam("unavailable_card"),
          code: EPaymentModeDisableDetailsType.UNAVAILABLE_CARD,
        });
      }
      if (card.temporary && excludeTemporary) return;
      cardsMap.push({
        id: value,
        id_payment_mode,
        value,
        params: cardParams,
        content: cardLabel,
        name: cardLabel,
        canPreset: !disabled && priority > 0,
        disabled,
        priority,
        warnings,
        id_picture,
        card: {
          id_brand,
          cbBrand,
          cardNum,
          expire,
        },
      });
    });
    return cardsMap;
  }

  getTokenizedCardPriority() {
    return parseInt(this.getParam("tokenized_card_priority") || "0") || 0;
  }

  getTokenizationFormPriority() {
    return parseInt(this.getParam("tokenization_form_priority") || "0") || 0;
  }

  getSelectDatasCanPreset<T = any>(): PaymentModeSelectDatas<T>[] {
    const selectDatas = this.getSelectDatas();
    return selectDatas.filter(
      ({ canPreset, disabled }) => !disabled && canPreset
    );
  }

  getSelectDatas<T = any>(): PaymentModeSelectDatas<T>[] {
    // retourne les options d'un mode de paiement
    // le cas simple, c'est une option par mode
    // pour Ogone, c'est en plus la liste des cartes enregistrées
    const model = this,
      id_payment_mode = model.id + "",
      provider = model.get("provider"),
      cart = app.getCart(),
      usedPaymentMode = cart.getUsedPaymentMode(),
      disableDetails = model.getDisableDetails(),
      disabled = disableDetails.length > 0;
    const ogoneDirectLabel = model.getParam("desc_title");
    const modelName = model.getName(),
      shortName = model.getShortName();

    const name =
      EPaymentProviders.OGONE_DIRECT_PROVIDER === provider
        ? ogoneDirectLabel || modelName
        : modelName;
    let isSelected =
        !!usedPaymentMode && +usedPaymentMode.id === +id_payment_mode,
      priority = model.get("priority") || 0;
    let canPreset = priority > 0;
    const baseOption: PaymentModeSelectDatas = {
        id: id_payment_mode,
        id_payment_mode,
        value: id_payment_mode,
        params: {},
        content: model.getSelectOptionContent(disableDetails),
        warnings: disableDetails,
        name: shortName || name,
        disabled: disabled,
        selected: isSelected,
        canPreset,
        id_picture: model.getPicture()?.id_object_picture || "",
        deletable: false,
        ignorePreset: false,
        priority,
      },
      selectDatas: PaymentModeSelectDatas[] = [];
    const isOgoneClassic = provider === EPaymentProviders.OGONE_PROVIDER;
    const isOgoneDirect = provider === EPaymentProviders.OGONE_DIRECT_PROVIDER;
    if (isOgoneClassic || isOgoneDirect) {
      //var filterBrandIds = brands.pluck('brand');

      const is_tokenizer_open = model.getOption("tokenizer", false);
      const cards = model.getCards(true),
        paymentBrandsAll = model.getBrands(),
        paymentBrandsStorable = model.getBrands("Y"),
        paymentBrandsNotStorable = model.getBrands("N"),
        hasSomeBrandStorable = paymentBrandsStorable.length > 0,
        id_brand_selected = model.getOption("id_brand", false, false),
        id_card_selected = model.getOption("id_card", false, true);
      let ignorePresetBecauseNoCard = false;
      if (hasSomeBrandStorable) {
        // si on n'a pas de carte enregistrée, et qu'on peut en enregistrer, on ignore la sélection
        // sur Ogone Legacy (attention pour le merge Ogone Direct, ça marchera différemment)
        ignorePresetBecauseNoCard = cards.length === 0;
        const tokenized_card_priority: number =
          model.getTokenizedCardPriority();
        cards.forEach((card) => {
          // TODO
          const id_card = card.params?.id_card as string,
            cardWarning = card.warnings || [],
            cardDisabled = card.disabled || disabled,
            isCardSelected =
              isSelected && id_card_selected === id_card && !is_tokenizer_open;
          if (isCardSelected) {
            baseOption.selected = false;
            isSelected = false;
          }
          if (card.disabled) {
          }
          const cardSelectData: PaymentModeSelectDatas = {
            ...card,
            ...{
              warnings: [...disableDetails, ...cardWarning],
              disabled: cardDisabled,
              selected: isCardSelected,
              deletable: true,
              completed: true,
              priority: tokenized_card_priority,
              options: {
                id_card,
              },
              // attr: ' data-card="' + id_card + '"', // deprecated
            },
          };
          selectDatas.push(cardSelectData);
        });
      }
      //
      if (isOgoneDirect) {
        const brands = paymentBrandsStorable.map((brand) => {
          const name = brand.get("name") || "";
          const shortName = brand.get("short_name") || name;
          return {
            id: brand.id,
            name,
            shortName,
            id_picture: brand.getIdPicture(),
          };
        });
        if (hasSomeBrandStorable) {
          const tokenization_form_priority =
            model.getTokenizationFormPriority();
          selectDatas.push({
            id: id_payment_mode + ":tokenizer",
            id_payment_mode,
            value: id_payment_mode + ":tokenizer",
            name: model.getParam("pay_with_tokenization_label") || name,
            mention: brands.map((brand) => brand.shortName).join(", "),
            id_picture: model.getPicture()?.id_object_picture || "",
            selected: is_tokenizer_open,
            disabled,
            priority: tokenization_form_priority,
            canPreset: tokenization_form_priority > 0,
            completed: false,
            warnings: disableDetails,
            hasCards: cards.length > 0,
            tokenizer: true,
            options: {
              brands,
              directlink: 1,
              tokenizer: true,
            },
          });
        }
      }
      //
      (isOgoneDirect ? paymentBrandsNotStorable : paymentBrandsAll).each(
        (brand) => {
          const brandId = brand.id + "";
          const selectId = id_payment_mode + ":" + brandId;
          const selected = isSelected && brandId === id_brand_selected;
          const directlink = brand.get("directlink") ? 1 : 0; // convert bool to int
          const ignorePreset = ignorePresetBecauseNoCard && directlink === 1;
          const apple_pay = !!brand.get("apple_pay");
          const priority = brand.getPriority();

          if (brand.isSupported()) {
            selectDatas.push({
              id: selectId,
              id_payment_mode: id_payment_mode as string,
              value: selectId,
              name: brand.getShortName(),
              id_picture: brand.getIdPicture(),
              selected,
              disabled,
              canPreset: priority > 0,
              priority,
              warnings: disableDetails,
              completed: !directlink,
              apple_pay,
              ignorePreset,
              options: {
                id_brand: brandId,
                brand: brand.getBrand(),
                directlink,
              },
              ...(!directlink && {
                params: {
                  directlink,
                },
              }),
            });
          }
        }
      );
    } else if (provider === EPaymentProviders.EDENRED_PROVIDER) {
      const buttonDesc = EdenredDataModel.getInstance().getButtonOptions();
      const { checked } = buttonDesc;
      selectDatas.push({
        ...baseOption,
        ...{
          selected: checked,
          options: buttonDesc,
        },
      });
    } else {
      selectDatas.push(baseOption);
    }
    return selectDatas;
  }
  // getSelectOptions() {
  //   const selectDatas = this.getSelectDatas();
  //   return selectDatas
  //     .map(function (o) {
  //       return selectOptionTemplate(o);
  //     })
  //     .join("");
  // }

  formatMoneyReturn(return_money?: string) {
    const model = this;
    let param_key = "return_money_vae_message";
    if (app.getCart().isVAD()) {
      if (!return_money) {
        param_key = "return_money_label";
      } else if (return_money === "0") {
        param_key = "return_money_0_vad_message";
      } else {
        param_key = "return_money_vad_message";
      }
    } else {
      return ""; // en VAE, on affiche rien
    }
    return (model.getParam(param_key) || "").replace(
      "%s",
      formatPrice(return_money)
    );
  }

  getName() {
    return this.get("name") || "-";
  }

  getShortName(): string {
    const model = this,
      short_name = model.get("short_name"),
      shortName = short_name || "";
    return shortName;
  }

  load3DSv2Params() {
    // Ogone 3DS v2 extra params
    var navigator = window.navigator,
      screen = window.screen,
      extraParams: Record<string, any> = {};
    //
    function setBrowserInfo(name: string, value: any) {
      extraParams["browser" + name] = value;
    }
    try {
      setBrowserInfo("JavaEnabled", navigator.javaEnabled() || false);
      setBrowserInfo("TimeZone", new Date().getTimezoneOffset());
      setBrowserInfo("ColorDepth", screen.colorDepth);
      setBrowserInfo("Language", navigator.language);
      setBrowserInfo("ScreenWidth", screen.width);
      setBrowserInfo("ScreenHeight", screen.height);
      setBrowserInfo("UserAgent", navigator.userAgent);
    } catch (err) {}
    this.setOption("extraParams", extraParams);
  }

  getDisableDetails() {
    const details: PMDisabledDetails[] = [],
      cart = app.getCart(),
      EdenredSelfSuffisant = EdenredDataModel.isSelfSuffisant(),
      EdenredIsSelected = EdenredDataModel.isSelected(),
      requireAltPM = EdenredDataModel.requireAltPM(),
      can_use_in_multi_payment = this.getParam("can_use_in_multi_payment"),
      isVAD = cart.isVAD(),
      deliveryOptionsAvailable = !!this.get(isVAD ? "vad" : "vae");
    if (!deliveryOptionsAvailable) {
      if (isVAD) {
        details.push({
          message: app.t("Indisponible en livraison", "Checkout"),
          code: EPaymentModeDisableDetailsType.UNAVAILABLE_VAD,
        });
      } else {
        details.push({
          message: app.t("Indisponible en Click & Collect", "Checkout"),
          code: EPaymentModeDisableDetailsType.UNAVAILABLE_VAE,
        });
      }
    }
    if (EdenredIsSelected && requireAltPM && !can_use_in_multi_payment) {
      details.push({
        message: app.t(
          "Il est impossible d’utiliser ce mode de paiement en complément d’un titre restaurant",
          "Checkout"
        ),
        code: EPaymentModeDisableDetailsType.MULTI_PAYMENT_INVALID,
      });
    }
    if (EdenredSelfSuffisant && !EdenredDataModel.isSelf(this)) {
      details.push({
        message: app.t(
          "L'intégralité de la commande peut être payée avec Edenred",
          "Checkout"
        ),
        code: EPaymentModeDisableDetailsType.EDENRED_SELF_SUFFISANT,
      });
    }
    if (!this.get("provider")) {
      details.push({
        message: "Invalid Provider",
        code: EPaymentModeDisableDetailsType.INVALID_PROVIDER,
      });
    }
    if (this.hasProductRestriction()) {
      details.push({
        message: this.getRestrictionMessage(),
        code: EPaymentModeDisableDetailsType.PRODUCT_RESTRICTION,
      });
    }
    return details;
  }

  getSelectOptionContent(disableDetails: any[]) {
    const model = this;
    let html = model.get("name"),
      name = html,
      picture = model.getPicture(),
      pictureUrl;

    if (picture) {
      pictureUrl = UrlFactory.getUrlPicture(
        picture.id_object_picture,
        "paymentmode",
        "100x60",
        "payment",
        "png"
      );
      if (pictureUrl) {
        html = "<img class='payment-icon' src='" + pictureUrl + "' /> " + name;
      }
    }
    if (disableDetails.length) {
      html +=
        "<div class='payment-mode__selection__alert'><p>" +
        disableDetails.join("</p><p>") +
        "</p></div>";
    }
    return html;
  }

  resetOptions(paramsDefaults: any = {}, optionsDefaults: any = {}) {
    this.set("options", {
      id_payment_mode: this.id,
      params: paramsDefaults,
      ...optionsDefaults,
    });
  }

  getOptions() {
    return this.get("options");
  }

  getOption(
    propName: string,
    defaultValue?: string | boolean | null,
    isParams?: boolean
  ) {
    const oo = this.getOptions(),
      o = (isParams ? oo?.params : oo)[propName];
    if (_.isUndefined(o)) return defaultValue;
    return o;
  }

  setOption(propName: string, propValue: any, isParams?: boolean) {
    const oo = this.getOptions(),
      v = ((isParams ? oo?.params : oo)[propName] = propValue);
    return v;
  }

  setOptions(params: any, isParams?: boolean) {
    const oo = this.getOptions();
    _(params).each(function (v, k) {
      (isParams ? oo?.params : oo)[k] = v;
    });
  }

  getPicture() {
    const model = this,
      pictures = model.get("pictures"),
      picture = pictures && pictures[0];
    return picture;
  }
  getIdPicture() {
    return this.getPicture()?.id_object_picture || "";
  }

  getDesc() {
    const model = this,
      provider = model.get("provider"),
      modelName = model.getName();
    let desc_primary: string = modelName || "",
      desc_secondary = "",
      dark = false,
      id_picture: string = model.getIdPicture(),
      id_payment_mode: string = model.id + "",
      brand_name: string = "",
      change_label = app.t("Modifier", "UI"),
      disabled = false,
      warnings = model.getDisableDetails(),
      customer_card: CustomerCard | undefined;
    switch (provider) {
      case EPaymentProviders.OGONE_PROVIDER:
      case EPaymentProviders.OGONE_DIRECT_PROVIDER:
        const id_card = model.getOption("id_card", false, true),
          id_brand = model.getOption("id_brand", false, false),
          brands = app.getPaymentBrands(),
          directlink = model.getOption("directlink", null, true);
        customer_card = app.getCustomer().getCard(id_card);
        let cbBrand = model.getOption("cbBrand", null, true),
          brand = id_brand ? brands.get(id_brand) : null,
          brandName;
        if (id_card && customer_card) {
          const expire = formatExpire(customer_card),
            cardNum = formatCardName(customer_card.cardNum);
          cbBrand = customer_card.brand;
          desc_secondary = cardNum + " " + expire;
        } else if (directlink === 0) {
          const brandName = brand?.get("name");
          if (brandName) {
            desc_primary = brandName;
          }
          desc_secondary = model.getParam("you_will_be_redirect");
        } else if (brand?.isApplePay() || brand?.isGooglePay()) {
          desc_primary =
            model.getParam("desc_title") || brand?.get("name") || modelName;
        } else {
          desc_primary = model.getParam("desc_title") || modelName;
        }
        if (cbBrand) {
          brand = brands.find((b) => {
            // retro-compatibilité projet ORM
            return (
              b.getBrand() === cbBrand &&
              +(b.get("id_payment_mode") || 0) === +id_payment_mode
            );
          });
          if (brand) {
            brandName = brand ? brand.getShortName() : cbBrand;
            desc_primary = brandName;
            id_picture = brand.getIdPicture() || id_picture;
            if (!(model.getParam("brand") || []).includes(brand.id)) {
              warnings.push(model.getParam("unavailable_card"));
            }
            if (id_card && customer_card) {
              desc_primary += " " + formatCardName(customer_card.cardNum, true);
            }
          }
        } else if (id_brand) {
          brand = brands.find((paymentBrand) => +paymentBrand.id === +id_brand);
          brand_name = brand?.getShortName() || desc_primary;
          id_picture = brand?.getIdPicture() || id_picture;
        }
        break;
      case EPaymentProviders.CASH_ON_DELIVERY_PROVIDER:
        const return_money = model.getOption("return_money", "");
        let param_key = "return_money_vae_message";
        if (!!return_money) {
          param_key = "return_money_vad_message";
        } else if (app.getCart().isVAD()) {
          param_key = "return_money_0_vad_message";
        }
        desc_secondary = (model.getParam(param_key) || "").replace(
          "%s",
          formatPrice(return_money)
        );
        break;
      case EPaymentProviders.ZERO_PROVIDER:
        disabled = true;
        desc_primary = model.getParam("desc_primary");
        desc_secondary = model.getParam("desc_secondary");
        change_label = "";
        dark = true;
        break;
    }
    return {
      desc_primary,
      desc_secondary,
      brand_name,
      id_picture,
      change_label,
      warnings,
      dark,
      disabled,
      customer_card,
    };
  }

  getSummary() {
    const model = this,
      provider = model.get("provider"),
      desc = model.getDesc(),
      desc_primary = desc?.desc_primary || "";
    if (provider === EPaymentProviders.OGONE_DIRECT_PROVIDER) {
      console.log(desc);
      return desc.brand_name || desc_primary;
    } else {
      return desc_primary;
    }
  }

  isApplePayNative() {
    return this.isPaymentNative(EPaymentNative.APPLE_PAY);
  }
  isGooglePayNative() {
    return this.isPaymentNative(EPaymentNative.GOOGLE_PAY);
  }

  resetMobilePaymentOptions() {
    const model = this;
    if (model.isApplePayNative()) {
      const applePaySession = model.getOption(
        "applePaySession"
      ) as ApplePaySession;
      if (applePaySession) {
        applePaySession.abort();
      }
      model.setOption("applePayEncryptedDatas", null, true);
    } else if (model.isGooglePayNative()) {
      model.setOption("googlePayEncryptedDatas", null, true);
    }
  }

  isPaymentNative(type: EPaymentNative) {
    const model = this;
    if (model.get("provider") === EPaymentProviders.OGONE_DIRECT_PROVIDER) {
      const id_brand = model.getOption("id_brand", false, false);
      const brands = model.getBrands();
      const brand = brands?.find((b) => +b.id === +id_brand);
      return (
        (type === EPaymentNative.APPLE_PAY
          ? brand?.isApplePay()
          : brand?.isGooglePay()) && brand?.isDirectLink()
      );
    }
    return false;
  }

  getGooglePayProps() {
    const model = this;
    const id_brand = model.getOption("id_brand", false, false);
    const brands = model.getBrands();
    const brand = brands?.find((b) => +b.id === +id_brand);
    return brand?.get("googlePayProps") as GooglePayProps;
  }

  getRequirement() {
    const model = this,
      id_payment_mode = model.id,
      cart = app.getCart(),
      isVAD = cart.isVAD(),
      provider = model.get("provider");
    let requirement = PROVIDER_REQUIRE.NOTHING;
    if (!model.get("vad") && isVAD) {
      return PROVIDER_REQUIRE.MODE_VAD_UNAVAILABLE;
    } else if (!model.get("vae") && !isVAD) {
      return PROVIDER_REQUIRE.MODE_VAE_UNAVAILABLE;
    }
    switch (provider) {
      case EPaymentProviders.OGONE_PROVIDER:
      case EPaymentProviders.OGONE_DIRECT_PROVIDER:
        const isOgoneDirect =
          provider === EPaymentProviders.OGONE_DIRECT_PROVIDER;
        const id_card = model.getOption("id_card", false, true);
        const cvc = model.getOption("cvc", false, true),
          validationCVC = /^[0-9]{3,4}$/.test(cvc),
          cartRequireCVC = cart.requireCVC(),
          directlink = model.getOption("directlink", false, true),
          cbBrand = model.getOption("cbBrand", null, true);
        model.load3DSv2Params();
        const id_brand = model.getOption("id_brand", null, false);
        const brands = model.getBrands(),
          brand = brands.findWhere(
            (b: PaymentBrandModel) => +b.id === +id_brand
          );
        if (directlink === 0) {
          // noop
        } else if (brand?.isApplePay() && brand?.isDirectLink()) {
          const applePayEncryptedDatas = model.getOption(
            "applePayEncryptedDatas",
            null,
            true
          );
          if (!applePayEncryptedDatas) {
            requirement = PROVIDER_REQUIRE.APPLE_PAY_AWAITING_START;
          }
        } else if (brand?.isGooglePay() && brand?.isDirectLink()) {
          const googlePayEncryptedDatas = model.getOption(
            "googlePayEncryptedDatas",
            null,
            true
          );
          if (!googlePayEncryptedDatas) {
            requirement = PROVIDER_REQUIRE.GOOGLE_PAY_AWAITING_START;
          }
        } else if (!id_card) {
          // si on a pas de carte, on demande à l'ajouter
          if (isOgoneDirect) {
            if (!model.getOption("valid", false, false)) {
              requirement =
                PROVIDER_REQUIRE.OGONE_DIRECT_TOKENIZATION_FORM_VALIDATION;
            } else {
              requirement =
                PROVIDER_REQUIRE.OGONE_DIRECT_TOKENIZATION_SAVING_CARD;
            }
          } else if (cbBrand === null) {
            const brands = model.getBrands(),
              brandSize = brands.size();
            if (brandSize === 1) {
              // si il n'y a qu'une seule marque disponible on préselectionne celle-ci
              model.setOption("cbBrand", brands.first().get("brand"), true);
              requirement = PROVIDER_REQUIRE.OGONE_ADD_CARD;
            } else {
              requirement = PROVIDER_REQUIRE.OGONE_VALID_BRAND;
            }
          } else {
            requirement = PROVIDER_REQUIRE.OGONE_ADD_CARD;
          }
        } else {
          const card = app.getCustomer().getCard(id_card);
          if (!card || !card.brand) {
            requirement = PROVIDER_REQUIRE.OGONE_ADD_CARD;
          } else if (isCardExpired(card)) {
            requirement = PROVIDER_REQUIRE.OGONE_CARD_EXPIRED;
          } else if (
            // retro-compatibilité projet ORM,
            !model.getBrands("Y").find((b) => {
              return b.getBrand() === card.brand;
            })
            //{ brand: card.brand, id_payment_mode })
          ) {
            // si la brand n'est pas disponible
            requirement = PROVIDER_REQUIRE.OGONE_VALID_BRAND;
          } else if (!validationCVC) {
            // validation formelle du CVC
            if (isOgoneDirect) {
              const add_new_card = model.getOption("add_new_card", false, true);
              if (cartRequireCVC && !add_new_card) {
                requirement = PROVIDER_REQUIRE.OGONE_CVC;
              }
            } else if (cartRequireCVC || !card.used) {
              // si les règles panier ou que la carte n'a jamais été utilisée, on demande le CVC
              requirement = PROVIDER_REQUIRE.OGONE_CVC;
            }
          }
        }
        break;
      case EPaymentProviders.EDENRED_PROVIDER:
        if (EdenredDataModel.requireAltPM()) {
          requirement = PROVIDER_REQUIRE.REQUIRE_ALT_PM;
        }
        break;
      case EPaymentProviders.CASH_ON_DELIVERY_PROVIDER:
        // le champs monnaie à prévoir est uniquement en VAD
        const return_money = model.getOption("return_money");
        if (cart.isVAD() && !(return_money >= 0)) {
          requirement = PROVIDER_REQUIRE.RETURN_MONEY;
        }
        break;
      case EPaymentProviders.PAYTABS_PROVIDER:
        const primaryAddress = app.getCustomer().getPrimaryAddress();
        // si on on est Click & Collect et qu'on a pas de primary adress, on l'exige
        if (cart.isVAE() && !primaryAddress) {
          requirement = PROVIDER_REQUIRE.PRIMARY_ADDRESS;
        }
        break;
      case EPaymentProviders.ZERO_PROVIDER:
      default:
    }

    // multiple paiement with Edenred
    const canUseInMultiPayment = model.canUseInMultiPayment(),
      edenredPaymentMode = EdenredDataModel.getPM(),
      edenredPaymentModeId = edenredPaymentMode ? edenredPaymentMode.id : 0,
      isPrimaryEdenred = edenredPaymentModeId === model.id,
      edenredIsActive = EdenredDataModel.isActive(),
      use_multiple_payment =
        canUseInMultiPayment && edenredIsActive && !isPrimaryEdenred;
    let multiple_id_payment_mode = undefined,
      multiple_payment_available_amount = undefined;
    if (use_multiple_payment) {
      multiple_payment_available_amount = EdenredDataModel.getUsedAvailable();
      multiple_id_payment_mode = edenredPaymentModeId;
      model.setOption("use_multiple_payment", use_multiple_payment);
      model.setOption(
        "multiple_payment_available_amount",
        multiple_payment_available_amount
      );
      model.setOption("multiple_id_payment_mode", multiple_id_payment_mode);
    } else if (edenredIsActive && requirement === 0 && !isPrimaryEdenred) {
      // cas ou le paiement ne supporte pas le paiement multiple, mais que edenred a été ajouté entre temps.
      requirement = PROVIDER_REQUIRE.SIMPLE_PAYMENT;
    } else {
      const options = model.getOptions();
      delete options?.use_multiple_payment;
      delete options?.multiple_payment_available_amount;
      delete options?.multiple_id_payment_mode;
    }

    return requirement;
  }

  isAvailable(isVAE?: boolean) {
    return !!this.get(isVAE ? "vae" : "vad");
  }

  canPreset(isVAE: boolean) {
    const paymentMode = this,
      priority = paymentMode.get("priority") || 0,
      isAvailable = paymentMode.isAvailable(isVAE),
      dontHasRestriction = !paymentMode.hasProductRestriction(),
      provider = paymentMode.get("provider");
    let allowProvider = priority > 0;
    switch (provider) {
      case EPaymentProviders.OGONE_PROVIDER:
      case EPaymentProviders.OGONE_DIRECT_PROVIDER:
        const selectDatas = paymentMode.getSelectDatasCanPreset();
        allowProvider = selectDatas.length > 0;
        break;
    }
    return isAvailable && dontHasRestriction && allowProvider;
  }

  isZero() {
    return this.get("provider") === EPaymentProviders.ZERO_PROVIDER;
  }

  isOnlinePayment() {
    switch (this.get("provider")) {
      case EPaymentProviders.EDENRED_PROVIDER:
      case EPaymentProviders.OGONE_PROVIDER:
      case EPaymentProviders.OGONE_DIRECT_PROVIDER:
      case EPaymentProviders.PAYTABS_PROVIDER:
        return true;
      default:
        return false;
    }
  }

  getParam(key: string) {
    const params = this.get("params");
    return params && params[key];
  }

  getBrands(storepermently?: "Y" | "N") {
    const paymentMode = this,
      idBrands = paymentMode.getParam("brand"),
      brands = app.getPaymentBrands().byIds(idBrands);
    if (!storepermently) {
      storepermently = paymentMode.getOption("storepermently", null, true);
    }
    const isYes = storepermently === "Y",
      isNo = storepermently === "N";
    if (isYes || isNo) {
      // filter only directlink brands
      let toRemove: PaymentBrandModel[] = [];
      brands.each((b, i) => {
        const canTokenize =
          b.get("directlink") && !(b.isApplePay() || b.isGooglePay());
        if (canTokenize !== isYes) {
          toRemove.push(b);
        }
      });
      brands.remove(toRemove);
    }
    return brands;
  }

  getCard() {
    // Ogone Card
    const id_card = this.getOption("id_card", false, true);
    return app.getCustomer().getCard(id_card);
  }

  addCard(card: ICardOptions) {
    const paymentMode = this;
    card.temporary = !card.storing;
    app.getCustomer().addPaymentCard(card);
    paymentMode.setOption("id_card", card.id_card, true);
    paymentMode.setOption("cvc", "", true);
    paymentMode.setOption("card", card, false);
  }

  callbackError() {
    const paymentMode = this,
      provider = paymentMode.get("provider");
    if (provider === EPaymentProviders.OGONE_DIRECT_PROVIDER) {
      const add_new_card = paymentMode.getOption("add_new_card", false, true),
        card = paymentMode.getCard();
      if (add_new_card && card?.temporary && !card?.storing) {
        paymentMode.setOption("id_card", null, true);
        paymentMode.setOption("valid", false);
      }
    }
  }

  getCardBrand() {
    // Ogone Card
    const card = this.getCard();
    const brandName = card ? card.brand : null;
    const brands = this.getBrands();
    return !!brandName ? brands.findWhere({ brand: brandName }) || null : null;
  }

  getCvcLength(): number {
    return this.getCardBrand()?.getCvcLength() || 3;
  }
  canUseInMultiPayment() {
    return this.getParam("can_use_in_multi_payment");
  }

  validateBeforeSubmit(callback: (success: boolean) => void) {
    const requirement = this.getRequirement();
    if (requirement !== PROVIDER_REQUIRE.NOTHING) {
      callback(false);
      app.trigger("paymentModeRequirement", this, requirement);
      return false;
    }
    return true;
  }

  getRestrictionMessage() {
    const cart = app.getCart();
    const paymentRestrictions = cart.get("payment_restrictions");
    const restrictions = paymentRestrictions[this.id];
    let message: string = "",
      args;
    if (restrictions) {
      args = app.getProduct(restrictions[0])?.getName() || "N/A";
      if (restrictions.length > 1) {
        message =
          "Plusieurs produits présents dans votre panier ne sont pas éligibles avec ce mode de paiment (%s, …)";
      } else if (restrictions) {
        message = "%s n’est pas éligible avec ce mode de paiment";
      }
    }
    // if (EdenredDataModel.isActive() && !this.canUseInMultiPayment()) {
    //   message += " Indisponible avec Edenred.";
    // }
    return app.t(message, "Checkout", args);
  }

  hasProductRestriction() {
    // TODO
    // teste si le mode de paiment à un restriction lié à un produit (du panier)
    const cart = app.getCart();
    const model = this,
      paymentRestrictions = cart.get("payment_restrictions"),
      restriction = paymentRestrictions[model.id];

    return !_.isUndefined(restriction);
  }
}

PaymentModeModel.prototype.name = "PaymentModeModel";
PaymentModeModel.prototype.route = "apiPaymentModeModel";
PaymentModeModel.prototype.idAttribute = "id_payment_mode";
export default PaymentModeModel;
